import type { SectionId } from '../../../shared/types';
import { LEXICAL_SECTION_ID, NLP_SECTION_ID } from '../../../shared/types';
import type { ListMode } from '../../hooks';

export const NLP_UPSELL_LIST_ITEM_KEY = 'nlp-upsell';
export const STANDARD_UPSELL_LIST_ITEM_KEY = 'standard-upsell';

export type QuickSearchTabUpsellListItemKey =
  | typeof NLP_UPSELL_LIST_ITEM_KEY
  | typeof STANDARD_UPSELL_LIST_ITEM_KEY;

export type QuickSearchTabUpsellDescriptor = {
  count: number;
  key: QuickSearchTabUpsellListItemKey;
  targetSectionId: SectionId;
  variant: 'ai' | 'standard';
};

export type QuickSearchTabEmptyStateDescriptor = {
  activeTab: SectionId;
  otherTabCount: number;
  targetSectionId: SectionId;
};

type GetQuickSearchTabbedAffordancesProps = {
  activeTab: SectionId | null;
  hasQueriesSettled: boolean;
  listMode: ListMode;
  mediaIntelligenceResultsCount: number;
  standardResultsCount: number;
  visibleSectionId?: SectionId;
};

export function isQuickSearchTabUpsellListItemKey(
  value?: string | null
): value is QuickSearchTabUpsellListItemKey {
  return (
    value === NLP_UPSELL_LIST_ITEM_KEY ||
    value === STANDARD_UPSELL_LIST_ITEM_KEY
  );
}

export function getQuickSearchTabbedAffordances({
  activeTab,
  hasQueriesSettled,
  listMode,
  mediaIntelligenceResultsCount,
  standardResultsCount,
  visibleSectionId,
}: GetQuickSearchTabbedAffordancesProps): {
  emptyState: QuickSearchTabEmptyStateDescriptor | null;
  upsell: QuickSearchTabUpsellDescriptor | null;
} {
  if (listMode !== 'MULTI_TABBED' || !hasQueriesSettled) {
    return {
      emptyState: null,
      upsell: null,
    };
  }

  let emptyState: QuickSearchTabEmptyStateDescriptor | null = null;

  if (
    activeTab === LEXICAL_SECTION_ID &&
    standardResultsCount === 0 &&
    mediaIntelligenceResultsCount > 0
  ) {
    emptyState = {
      activeTab: LEXICAL_SECTION_ID,
      otherTabCount: mediaIntelligenceResultsCount,
      targetSectionId: NLP_SECTION_ID,
    };
  } else if (
    activeTab === NLP_SECTION_ID &&
    mediaIntelligenceResultsCount === 0 &&
    standardResultsCount > 0
  ) {
    emptyState = {
      activeTab: NLP_SECTION_ID,
      otherTabCount: standardResultsCount,
      targetSectionId: LEXICAL_SECTION_ID,
    };
  }

  let upsell: QuickSearchTabUpsellDescriptor | null = null;

  if (
    visibleSectionId === LEXICAL_SECTION_ID &&
    standardResultsCount > 0 &&
    mediaIntelligenceResultsCount > 0
  ) {
    upsell = {
      count: mediaIntelligenceResultsCount,
      key: NLP_UPSELL_LIST_ITEM_KEY,
      targetSectionId: NLP_SECTION_ID,
      variant: 'ai',
    };
  } else if (visibleSectionId === NLP_SECTION_ID && standardResultsCount > 0) {
    upsell = {
      count: standardResultsCount,
      key: STANDARD_UPSELL_LIST_ITEM_KEY,
      targetSectionId: LEXICAL_SECTION_ID,
      variant: 'standard',
    };
  }

  return {
    emptyState,
    upsell,
  };
}
